import { useState } from "react";
import { AppLayout } from "@project/components/AppLayout";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Switch } from "@project/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { Textarea } from "@project/components/ui/textarea";
import { User, Building, Bell, Shield, Key, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import { motion } from "framer-motion";
import {
  staggerContainer as container,
  fadeInUp as item,
} from "@project/lib/animations";
import { cn } from "@project/lib/utils";
import { toast } from "@project/hooks/use-toast";

type SettingsTab = "profile" | "company" | "notifications" | "security";

const settingsTabs = [
  { key: "profile" as SettingsTab, label: "Profile", icon: User },
  { key: "company" as SettingsTab, label: "Company", icon: Building },
  { key: "notifications" as SettingsTab, label: "Alerts", icon: Bell },
  { key: "security" as SettingsTab, label: "Security", icon: Shield },
];

const auditLog = [
  {
    action: "Job Created",
    user: "Admirra John",
    date: "14 Mar 2025",
    detail: "Sr. UX Designer",
  },
  {
    action: "Campaign Updated",
    user: "Manoj Shah",
    date: "13 Mar 2025",
    detail: "Q1 Engineering Drive",
  },
  {
    action: "User Added",
    user: "Admirra John",
    date: "12 Mar 2025",
    detail: "sarah.lee@company.com",
  },
  {
    action: "Settings Changed",
    user: "Admirra John",
    date: "11 Mar 2025",
    detail: "Notification preferences",
  },
  {
    action: "Job Closed",
    user: "Manoj Shah",
    date: "10 Mar 2025",
    detail: "Design Lead",
  },
  {
    action: "Password Changed",
    user: "Admirra John",
    date: "8 Mar 2025",
    detail: "Account security",
  },
];

const SettingsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<SettingsTab>("profile");
  const [emailNotif, setEmailNotif] = useState(true);
  const [pushNotif, setPushNotif] = useState(false);
  const [weeklyDigest, setWeeklyDigest] = useState(true);
  const [appUpdates, setAppUpdates] = useState(true);
  const [slackNotif, setSlackNotif] = useState(false);
  const [candidateUpdates, setCandidateUpdates] = useState(true);
  const [interviewReminder, setInterviewReminder] = useState(true);
  const [twoFA, setTwoFA] = useState(false);

  return (
    <AppLayout>
      <motion.div
        className="space-y-4"
        variants={container}
        initial="hidden"
        animate="show"
      >
        <motion.div variants={item}>
          <h1 className="font-display text-lg font-bold tracking-tight text-foreground">
            Settings
          </h1>
          <p className="mt-0.5 text-[12px] text-muted-foreground">
            Manage your account and preferences
          </p>
        </motion.div>

        <motion.div variants={item} className="flex flex-col gap-4 lg:flex-row">
          <div className="shrink-0 lg:w-48">
            <div className="no-scrollbar flex gap-1 overflow-x-auto lg:flex-col lg:overflow-visible">
              {settingsTabs.map(tab => {
                const active = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={cn(
                      "flex shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-3 py-2.5 text-left transition-all lg:w-full",
                      active
                        ? "border border-border/60 bg-card text-foreground shadow-sm"
                        : "text-muted-foreground hover:bg-card/50 hover:text-foreground"
                    )}
                  >
                    <tab.icon
                      className={cn(
                        "h-4 w-4 shrink-0",
                        active ? "text-primary" : "text-muted-foreground"
                      )}
                    />
                    <span
                      className={cn(
                        "text-[13px] font-medium",
                        active && "font-semibold"
                      )}
                    >
                      {tab.label}
                    </span>
                  </button>
                );
              })}
              <button
                onClick={() => {
                  toast({
                    title: "Logged out",
                    description: "You have been logged out successfully.",
                  });
                  navigate("/");
                }}
                className="mt-2 flex shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-3 py-2.5 text-left text-destructive transition-all hover:bg-destructive/10 sm:hidden lg:w-full"
              >
                <LogOut className="h-4 w-4 shrink-0" />
                <span className="text-[13px] font-medium">Logout</span>
              </button>
            </div>
          </div>

          <div className="min-w-0 flex-1">
            {/* ── Profile Tab ──────────────────── */}
            {activeTab === "profile" && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3"
              >
                <div className="section-card overflow-hidden">
                  <div className="border-b border-border/50 px-4 pb-3 pt-4">
                    <h2 className="font-display text-[14px] font-semibold text-foreground">
                      Personal Information
                    </h2>
                  </div>
                  <div className="p-4">
                    <div className="mb-5 flex items-center gap-3">
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primary-glow ring-4 ring-primary/10">
                        <span className="font-display text-lg font-bold text-primary-foreground">
                          AJ
                        </span>
                      </div>
                      <div>
                        <p className="font-display text-[13px] font-semibold text-foreground">
                          Admirra John
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          HR Admin · Bengaluru
                        </p>
                        <button className="mt-0.5 text-[11px] font-medium text-primary hover:underline">
                          Change avatar
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {[
                        { label: "First Name", value: "Admirra" },
                        { label: "Last Name", value: "John" },
                        {
                          label: "Email",
                          value: "admirra@universalsoftware.com",
                        },
                        { label: "Phone", value: "+91 98765 43210" },
                        { label: "Job Title", value: "HR Admin" },
                        {
                          label: "Employee ID",
                          value: "EMP-0042",
                          disabled: true,
                        },
                      ].map(f => (
                        <div key={f.label} className="space-y-1">
                          <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {f.label}
                          </Label>
                          <Input
                            defaultValue={f.value}
                            className="h-9 rounded-lg bg-background text-[13px]"
                            disabled={f.disabled}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="section-card overflow-hidden">
                  <div className="border-b border-border/50 px-4 pb-3 pt-4">
                    <h2 className="font-display text-[14px] font-semibold text-foreground">
                      Preferences
                    </h2>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="space-y-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Timezone
                        </Label>
                        <Select defaultValue="ist">
                          <SelectTrigger className="h-9 rounded-lg bg-background text-[13px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ist">IST (UTC+5:30)</SelectItem>
                            <SelectItem value="pst">PST (UTC-8)</SelectItem>
                            <SelectItem value="est">EST (UTC-5)</SelectItem>
                            <SelectItem value="gmt">GMT (UTC+0)</SelectItem>
                            <SelectItem value="cet">CET (UTC+1)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Language
                        </Label>
                        <Select defaultValue="en">
                          <SelectTrigger className="h-9 rounded-lg bg-background text-[13px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="en">English</SelectItem>
                            <SelectItem value="hi">Hindi</SelectItem>
                            <SelectItem value="es">Spanish</SelectItem>
                            <SelectItem value="fr">French</SelectItem>
                            <SelectItem value="de">German</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Date Format
                        </Label>
                        <Select defaultValue="dd-mm-yyyy">
                          <SelectTrigger className="h-9 rounded-lg bg-background text-[13px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="dd-mm-yyyy">
                              DD/MM/YYYY
                            </SelectItem>
                            <SelectItem value="mm-dd-yyyy">
                              MM/DD/YYYY
                            </SelectItem>
                            <SelectItem value="yyyy-mm-dd">
                              YYYY-MM-DD
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          LinkedIn Profile
                        </Label>
                        <Input
                          defaultValue="https://linkedin.com/in/admirrajohn"
                          className="h-9 rounded-lg bg-background text-[13px]"
                        />
                      </div>
                    </div>
                    <Button
                      className="btn-glow mt-4 h-8 rounded-lg text-[12px] font-semibold"
                      onClick={() =>
                        toast({
                          title: "Saved",
                          description: "Profile preferences updated.",
                        })
                      }
                    >
                      Save Changes
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── Company Tab ──────────────────── */}
            {activeTab === "company" && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3"
              >
                <div className="section-card overflow-hidden">
                  <div className="border-b border-border/50 px-4 pb-3 pt-4">
                    <h2 className="font-display text-[14px] font-semibold text-foreground">
                      Company Details
                    </h2>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {[
                        { label: "Company Name", value: "Universal Software" },
                        {
                          label: "Legal Entity Name",
                          value: "Universal Software Pvt. Ltd.",
                        },
                        { label: "Industry", value: "Technology" },
                        {
                          label: "Website",
                          value: "https://universalsoftware.com",
                        },
                        { label: "HQ Location", value: "Bengaluru, India" },
                        { label: "Founded Year", value: "2015" },
                      ].map(f => (
                        <div key={f.label} className="space-y-1">
                          <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {f.label}
                          </Label>
                          <Input
                            defaultValue={f.value}
                            className="h-9 rounded-lg bg-background text-[13px]"
                          />
                        </div>
                      ))}
                      <div className="space-y-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Company Size
                        </Label>
                        <Select defaultValue="201-500">
                          <SelectTrigger className="h-9 rounded-lg bg-background text-[13px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1-50">1-50</SelectItem>
                            <SelectItem value="51-200">51-200</SelectItem>
                            <SelectItem value="201-500">201-500</SelectItem>
                            <SelectItem value="501-1000">501-1000</SelectItem>
                            <SelectItem value="1000+">1000+</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Company Type
                        </Label>
                        <Select defaultValue="private">
                          <SelectTrigger className="h-9 rounded-lg bg-background text-[13px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="private">Private</SelectItem>
                            <SelectItem value="public">Public</SelectItem>
                            <SelectItem value="startup">Startup</SelectItem>
                            <SelectItem value="nonprofit">
                              Non-Profit
                            </SelectItem>
                            <SelectItem value="government">
                              Government
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="section-card overflow-hidden">
                  <div className="border-b border-border/50 px-4 pb-3 pt-4">
                    <h2 className="font-display text-[14px] font-semibold text-foreground">
                      Branding & Social
                    </h2>
                  </div>
                  <div className="space-y-3 p-4">
                    <div className="space-y-1">
                      <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Company Description
                      </Label>
                      <Textarea
                        defaultValue="Universal Software is a technology company building innovative HR and recruitment solutions."
                        className="min-h-[60px] rounded-lg bg-background text-[13px]"
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="space-y-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Logo URL
                        </Label>
                        <Input
                          defaultValue="https://universalsoftware.com/logo.png"
                          className="h-9 rounded-lg bg-background text-[13px]"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Careers Page URL
                        </Label>
                        <Input
                          defaultValue="https://universalsoftware.com/careers"
                          className="h-9 rounded-lg bg-background text-[13px]"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          LinkedIn
                        </Label>
                        <Input
                          defaultValue="https://linkedin.com/company/universalsoftware"
                          className="h-9 rounded-lg bg-background text-[13px]"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Glassdoor
                        </Label>
                        <Input
                          placeholder="https://glassdoor.com/..."
                          className="h-9 rounded-lg bg-background text-[13px]"
                        />
                      </div>
                    </div>
                    <Button
                      className="mt-2 h-8 rounded-lg text-[12px] font-semibold"
                      onClick={() =>
                        toast({
                          title: "Saved",
                          description: "Company details updated.",
                        })
                      }
                    >
                      Update Company
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── Notifications Tab ──────────────────── */}
            {activeTab === "notifications" && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3"
              >
                <div className="section-card overflow-hidden">
                  <div className="border-b border-border/50 px-4 pb-3 pt-4">
                    <h2 className="font-display text-[14px] font-semibold text-foreground">
                      General Notifications
                    </h2>
                  </div>
                  <div className="space-y-0.5 p-4">
                    {[
                      {
                        label: "Email Notifications",
                        desc: "Receive updates via email",
                        checked: emailNotif,
                        onChange: setEmailNotif,
                      },
                      {
                        label: "Push Notifications",
                        desc: "Browser push notifications",
                        checked: pushNotif,
                        onChange: setPushNotif,
                      },
                      {
                        label: "Slack Notifications",
                        desc: "Get alerts in Slack channel",
                        checked: slackNotif,
                        onChange: setSlackNotif,
                      },
                      {
                        label: "Weekly Digest",
                        desc: "Summary of weekly activity",
                        checked: weeklyDigest,
                        onChange: setWeeklyDigest,
                      },
                      {
                        label: "App Updates",
                        desc: "New features & release notes",
                        checked: appUpdates,
                        onChange: setAppUpdates,
                      },
                    ].map(n => (
                      <div
                        key={n.label}
                        className="flex items-center justify-between rounded-xl p-3 transition-colors hover:bg-accent/20"
                      >
                        <div>
                          <p className="text-[13px] font-semibold text-foreground">
                            {n.label}
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            {n.desc}
                          </p>
                        </div>
                        <Switch
                          checked={n.checked}
                          onCheckedChange={n.onChange}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="section-card overflow-hidden">
                  <div className="border-b border-border/50 px-4 pb-3 pt-4">
                    <h2 className="font-display text-[14px] font-semibold text-foreground">
                      Recruitment Alerts
                    </h2>
                  </div>
                  <div className="space-y-0.5 p-4">
                    {[
                      {
                        label: "New Application Received",
                        desc: "When a candidate applies to your job",
                        checked: candidateUpdates,
                        onChange: setCandidateUpdates,
                      },
                      {
                        label: "Interview Reminders",
                        desc: "30 min before scheduled interviews",
                        checked: interviewReminder,
                        onChange: setInterviewReminder,
                      },
                      {
                        label: "Offer Accepted/Rejected",
                        desc: "When a candidate responds to an offer",
                        checked: true,
                        onChange: () => {},
                      },
                      {
                        label: "Job Posting Expiry",
                        desc: "3 days before a posting expires",
                        checked: true,
                        onChange: () => {},
                      },
                      {
                        label: "Referral Status Updates",
                        desc: "When referred candidates move stages",
                        checked: false,
                        onChange: () => {},
                      },
                    ].map(n => (
                      <div
                        key={n.label}
                        className="flex items-center justify-between rounded-xl p-3 transition-colors hover:bg-accent/20"
                      >
                        <div>
                          <p className="text-[13px] font-semibold text-foreground">
                            {n.label}
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            {n.desc}
                          </p>
                        </div>
                        <Switch
                          checked={n.checked}
                          onCheckedChange={n.onChange}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── Security Tab ──────────────────── */}
            {activeTab === "security" && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3"
              >
                <div className="section-card overflow-hidden">
                  <div className="border-b border-border/50 px-4 pb-3 pt-4">
                    <h2 className="font-display text-[14px] font-semibold text-foreground">
                      Password & Authentication
                    </h2>
                  </div>
                  <div className="space-y-3 p-4">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="space-y-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Current Password
                        </Label>
                        <Input
                          type="password"
                          placeholder="••••••••"
                          className="h-9 rounded-lg bg-background text-[13px]"
                        />
                      </div>
                      <div />
                      <div className="space-y-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          New Password
                        </Label>
                        <Input
                          type="password"
                          placeholder="Min 8 characters"
                          className="h-9 rounded-lg bg-background text-[13px]"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Confirm New Password
                        </Label>
                        <Input
                          type="password"
                          placeholder="Re-enter password"
                          className="h-9 rounded-lg bg-background text-[13px]"
                        />
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      className="h-8 rounded-lg text-[12px] font-semibold"
                      onClick={() =>
                        toast({
                          title: "Updated",
                          description: "Password changed successfully.",
                        })
                      }
                    >
                      Update Password
                    </Button>
                  </div>
                </div>

                <div className="section-card overflow-hidden">
                  <div className="border-b border-border/50 px-4 pb-3 pt-4">
                    <h2 className="font-display text-[14px] font-semibold text-foreground">
                      Two-Factor Authentication
                    </h2>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between rounded-xl border border-border/30 bg-muted/30 p-3">
                      <div>
                        <p className="text-[13px] font-semibold text-foreground">
                          Enable 2FA
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          Add an extra layer of security with authenticator app
                        </p>
                      </div>
                      <Switch checked={twoFA} onCheckedChange={setTwoFA} />
                    </div>
                    <div className="mt-3 space-y-1">
                      <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Session Timeout
                      </Label>
                      <Select defaultValue="30">
                        <SelectTrigger className="h-9 max-w-xs rounded-lg bg-background text-[13px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="15">15 minutes</SelectItem>
                          <SelectItem value="30">30 minutes</SelectItem>
                          <SelectItem value="60">1 hour</SelectItem>
                          <SelectItem value="480">8 hours</SelectItem>
                          <SelectItem value="never">Never</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="section-card overflow-hidden">
                  <div className="border-b border-border/50 px-4 pb-3 pt-4">
                    <h2 className="font-display text-[14px] font-semibold text-foreground">
                      Audit Log
                    </h2>
                  </div>
                  {/* Mobile */}
                  <div className="divide-y divide-border/30 sm:hidden">
                    {auditLog.map((log, i) => (
                      <div key={i} className="px-4 py-3">
                        <div className="flex items-center justify-between">
                          <p className="text-[12px] font-medium text-foreground">
                            {log.action}
                          </p>
                          <p className="text-[10px] text-muted-foreground">
                            {log.date}
                          </p>
                        </div>
                        <p className="mt-0.5 text-[11px] text-muted-foreground">
                          {log.user} · {log.detail}
                        </p>
                      </div>
                    ))}
                  </div>
                  {/* Desktop */}
                  <div className="hidden overflow-x-auto sm:block">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-border/50 bg-muted/30 hover:bg-muted/30">
                          {["Action", "User", "Date", "Detail"].map(h => (
                            <TableHead
                              key={h}
                              className="py-3 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/50"
                            >
                              {h}
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {auditLog.map((log, i) => (
                          <TableRow
                            key={i}
                            className="table-row-hover border-b border-border/30 last:border-0"
                          >
                            <TableCell className="py-3 text-[12px] font-medium text-foreground">
                              {log.action}
                            </TableCell>
                            <TableCell className="py-3 text-[12px] text-muted-foreground">
                              {log.user}
                            </TableCell>
                            <TableCell className="py-3 text-[12px] text-muted-foreground">
                              {log.date}
                            </TableCell>
                            <TableCell className="py-3 text-[12px] text-muted-foreground">
                              {log.detail}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AppLayout>
  );
};

export default SettingsPage;
